import { fetchAuthSession } from "aws-amplify/auth";
import { apiFetch } from "../api.js";
import { env } from "../env.js";

/**
 * Hearth-neutral SSE event types — must mirror `ai/src/providers/types.ts`
 * StreamEvent + the chat-stream Lambda's `tool_result` extension. Web
 * cannot import from @hearth/shared (5.1 lock); this is the parallel.
 */
export type AiStreamEvent =
  | { type: "text_delta"; delta: string }
  | { type: "tool_use_start"; callId: string; toolName: string }
  | { type: "tool_use_input_delta"; callId: string; partialJson: string }
  | {
      type: "tool_use_end";
      callId: string;
      arguments: Record<string, unknown>;
    }
  | {
      type: "tool_result";
      callId: string;
      toolName: string;
      result?: unknown;
      error?: string;
      citedAuthority?: string;
      durationMs: number;
    }
  | { type: "usage"; inputTokens: number; outputTokens: number }
  | {
      type: "done";
      stopReason: "end_turn" | "tool_use" | "max_tokens" | "error";
    }
  | { type: "error"; message: string };

export type AiProvider = "claude" | "openai" | "bedrock";
export type AiBillingMode = "hearth" | "byok";

export interface AiConversation {
  user_id: string;
  conversation_id: string;
  household_id: string;
  title: string;
  last_message_at: string;
  archived: boolean;
  provider: AiProvider;
  model: string;
  billing_mode: AiBillingMode;
  total_input_tokens: number;
  total_output_tokens: number;
}

export interface AiMessage {
  conversation_id: string;
  message_id: string;
  timestamp: string;
  role: "user" | "assistant" | "tool";
  content: string;
  tool_calls?: ReadonlyArray<{
    call_id: string;
    tool_name: string;
    arguments: Record<string, unknown>;
  }>;
  tool_call_id?: string;
  provider: AiProvider;
  model: string;
  contains_tax_data: boolean;
}

export interface AiUsage {
  month_start: string;
  month_to_date: string;
  cap: number;
  hearth_tokens_used: number;
  hearth_tokens_remaining: number;
  cap_pct: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_cents: number;
  total_request_count: number;
  breakdown: Record<
    string,
    {
      input_tokens: number;
      output_tokens: number;
      cost_cents: number;
      request_count: number;
    }
  >;
}

export interface AiProvidersResponse {
  active_provider: AiProvider;
  active_billing_mode: AiBillingMode;
  monthly_token_cap: number;
  providers: ReadonlyArray<{
    name: AiProvider;
    display_name: string;
    default_model: string;
    byok_configured: boolean;
    byok_supported?: boolean;
    available_models: ReadonlyArray<string>;
  }>;
  pricing: ReadonlyArray<{
    provider: AiProvider;
    model: string;
    inputCentsPerMillion: number;
    outputCentsPerMillion: number;
  }>;
  price_snapshot_date: string;
}

// === REST endpoints =========================================================

export function listConversations(): Promise<{ conversations: AiConversation[] }> {
  return apiFetch("/v1/ai/conversations");
}

export function getConversation(
  conversation_id: string,
): Promise<{ conversation: AiConversation; messages: AiMessage[] }> {
  return apiFetch(`/v1/ai/conversations/${encodeURIComponent(conversation_id)}`);
}

export function createConversation(
  body: { title?: string; household_id?: string } = {},
): Promise<{ conversation: AiConversation }> {
  return apiFetch("/v1/ai/conversations", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteConversation(
  conversation_id: string,
): Promise<{ ok: boolean }> {
  return apiFetch(`/v1/ai/conversations/${encodeURIComponent(conversation_id)}`, {
    method: "DELETE",
  });
}

export function getUsage(): Promise<AiUsage> {
  return apiFetch("/v1/ai/usage");
}

export function getProviders(): Promise<AiProvidersResponse> {
  return apiFetch("/v1/ai/providers");
}

export function setByokKey(body: {
  provider: "claude" | "openai";
  api_key: string;
}): Promise<{
  provider: string;
  ok: boolean;
  latency_ms: number;
  model_used_for_test?: string;
}> {
  return apiFetch("/v1/settings/ai/byok", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function deleteByokKey(provider: "claude" | "openai"): Promise<{
  provider: string;
  billing_mode_after: string;
}> {
  return apiFetch("/v1/settings/ai/byok", {
    method: "DELETE",
    body: JSON.stringify({ provider }),
  });
}

export function patchAiSettings(body: {
  ai_provider?: AiProvider;
  ai_billing_mode?: AiBillingMode;
  default_claude_model?: string;
  default_openai_model?: string;
  default_bedrock_model?: string;
  monthly_token_cap?: number;
}): Promise<{ ok: boolean; changes: number }> {
  return apiFetch("/v1/settings/ai", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// === Streaming chat =========================================================

export function isAiAvailable(): boolean {
  return !!env.aiChatStreamUrl;
}

/**
 * Open a chat stream against the AI Lambda Function URL. Returns an
 * async iterator that yields Hearth-neutral SSE events.
 *
 * The SPA UI consumes this via `for await (const ev of streamChat(...))`
 * and dispatches each event into local state.
 */
export async function* streamChat(args: {
  conversation_id: string;
  user_message: string;
  signal?: AbortSignal;
}): AsyncGenerator<AiStreamEvent, void, void> {
  const url = env.aiChatStreamUrl;
  if (!url) {
    throw new Error(
      "AI chat is not yet wired in this environment. Phase 8.1 deploy required.",
    );
  }
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error("Not authenticated");
  }
  const fetchInit: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      conversation_id: args.conversation_id,
      user_message: args.user_message,
    }),
  };
  if (args.signal) fetchInit.signal = args.signal;
  const res = await fetch(url, fetchInit);
  if (!res.ok || !res.body) {
    throw new Error(`AI stream HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE: events delimited by `\n\n`. Each event has lines like
    // `event: <type>` and `data: <json>`.
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const lines = block.split("\n");
      let eventName = "";
      let dataPayload = "";
      for (const line of lines) {
        if (line.startsWith("event: ")) eventName = line.slice("event: ".length);
        else if (line.startsWith("data: "))
          dataPayload = line.slice("data: ".length);
      }
      if (!eventName || !dataPayload) continue;
      try {
        const parsed = JSON.parse(dataPayload) as AiStreamEvent;
        yield parsed;
      } catch (err) {
        // Malformed event — surface as a neutral error so the UI can
        // recover rather than silently swallowing.
        yield {
          type: "error",
          message: `Malformed SSE event: ${err instanceof Error ? err.message : String(err)}`,
        };
      }
    }
  }
}
